/* tslint:disable:max-file-line-count */
import { inject } from 'mobx-react';
import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { AnalyticsStore, QueryRoute, RouteStore } from 'bernie-core';
import { Context } from 'bernie-context';
import { ErrorBoundary } from 'bernie-view';
import { Layer } from 'bernie-view';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { ESSAdapter, TypeaheadToolbarVersion, UitkTypeahead } from 'uitk-react-typeahead';

import { EssClientLogger } from 'typeahead-client-logger';
import { FormFieldBinding } from './form-field-binding';
import { SearchFormFieldProps } from 'components/common/search-form';

export interface LocationProps extends LocalizedComponentProps, SearchFormFieldProps {
  analytics?: AnalyticsStore;
  context?: Context;
  routeStore?: RouteStore;
  essAnalyticsLogger?: EssClientLogger;
}

// fields to reset on location change
export const RESET_FIELDS = [
  'poi', // Exp 28074: LS_Top_Attractions_Filter_by_Popularity
  'hotelName',
  'group'
];

@withLocalization
@FormFieldBinding
@inject('analytics', 'context', 'routeStore')
export class Location extends React.Component<LocationProps, {}> {
  private pristine = true;
  private fieldRef;

  constructor(props) {
    super(props);

    this.dismissTypeahead = this.dismissTypeahead.bind(this);
    this.openTypeahead = this.openTypeahead.bind(this);
    this.handleUpdate = this.handleUpdate.bind(this);
    this.validate = this.validate.bind(this);
    this.formValidator = this.formValidator.bind(this);
    this.renderTypeaheadDialog = this.renderTypeaheadDialog.bind(this);
    this.fieldRef = React.createRef();
    props.registerValidator(this.formValidator);
  }

  // tslint:disable no-multiple-render-methods
  public renderTypeaheadDialog(children) {
    return (
      <QueryRoute query={{ pwaDialog: 'typeahead' }}>
        <Layer id="home-typeahead" onDismiss={this.dismissTypeahead}>
          {children}
        </Layer>
      </QueryRoute>
    );
  }

  public dismissTypeahead() {
    const { analytics, routeStore } = this.props;
    if (analytics && typeof analytics.trackEvent === 'function') {
      analytics.trackEvent('DismissDestinationTypeahead', 'Destination Typeahead Modal Dismissed', analytics.omnitureData, null);
    }
    if (routeStore && typeof routeStore.goBack === 'function') {
      routeStore.goBack();
    }
    if (this.fieldRef && this.fieldRef.current && typeof this.fieldRef.current.focusTypeaheadInput === 'function') {
      this.fieldRef.current.focusTypeaheadInput();
    }
  }

  public openTypeahead() {
    const { analytics, routeStore } = this.props;

    if (routeStore && typeof routeStore.updateSearch === 'function') {
      routeStore.updateSearch({ pwaDialog: 'typeahead' });
    }

    if (analytics && typeof analytics.trackEvent === 'function') {
      analytics.trackEvent(':ChangeDestination', 'User searching Destination', {});
    }
  }

  public handleUpdate() {
    return selection => {
      const { updateModel, l10n } = this.props;
      const { formatDate } = l10n;

      const { data, term } = selection;
      const userHistory = data && data.category === 'USERHISTORY';

      if (typeof updateModel === 'function') {
        this.pristine = false;
        updateModel({
          target: {
            name: 'destination',
            value: term,
            track: true,
            userHistory
          }
        });
        updateModel({
          target: {
            name: 'latLong',
            value: data && data.location ? `${data.location.lat},${data.location.long}` : ''
          }
        });
        updateModel({
          target: {
            name: 'regionId',
            value: data && data.regionId ? data.regionId : ''
          }
        });
        updateModel({
          target: {
            name: 'selected',
            value: data && data.selected ? data.selected : ''
          }
        });
        if (data && data.checkindate && data.checkoutdate) {
          updateModel({
            target: {
              name: 'startDate',
              value: formatDate(new Date(data.checkindate), { raw: 'yyyy-MM-dd' })
            }
          });
          updateModel({
            target: {
              name: 'endDate',
              value: formatDate(new Date(data.checkoutdate), { raw: 'yyyy-MM-dd' })
            }
          });
        }

        RESET_FIELDS.forEach(name =>
          updateModel({
            target: {
              name,
              value: undefined
            }
          })
        );
      }
    };
  }

  public render() {
    const { context, l10n, model, wrapperClassNames } = this.props;
    const { formatText } = l10n;
    const destination = formatText('hotels.search.form.destination.label');

    const defaultEssAdapterConfig = {
      client: 'Hotels.Search',
      dest: true,
      essLogger: this.props.essAnalyticsLogger,
      expuserid: context && context.user && context.user.expUserId,
      features: 'ta_hierarchy|postal_code|contextual_ta|google',
      guid: context && context.deviceId,
      inputId: 'hotels-destination',
      lob: 'HOTELS',
      locale: context && context.locale,
      maxResults: 10,
      personalize: true,
      regionType: 2047,
      siteId: context && context.site && context.site.id
    };

    const defaultTypeaheadProps = {
      allowExactMatch: true,
      label: destination,
      icon: 'place',
      invalid: this.validate() && this.validate().text,
      name: 'destination',
      onClick: this.openTypeahead,
      onDismiss: this.dismissTypeahead,
      renderDialog: this.renderTypeaheadDialog,
      stid: 'hotels-destination',
      toolbarVersion: TypeaheadToolbarVersion.V1,
      value: model && model.destination,
      ref: this.fieldRef,
      onUpdate: this.handleUpdate()
    };

    return (
      <ErrorBoundary label="location.typeahead">
        <div className={wrapperClassNames || 'uitk-cell all-y-gutter-two m-cell-1-1 l-cell-1-3 xl-cell-1-3 location'}>
          <AbacusExperiment name="ESS_Two_Line_Display">
            <AbacusControl>
              <UitkTypeahead {...defaultTypeaheadProps} adapter={new ESSAdapter({ ...defaultEssAdapterConfig })} />
            </AbacusControl>
            <AbacusVariant bucket={1}>
              <UitkTypeahead
                {...defaultTypeaheadProps}
                adapter={
                  new ESSAdapter({
                    ...defaultEssAdapterConfig,
                    features: 'ta_hierarchy|postal_code|contextual_ta|google|consistent_display'
                  })
                }
              />
            </AbacusVariant>
            <AbacusVariant bucket={2}>
              <UitkTypeahead
                {...defaultTypeaheadProps}
                adapter={
                  new ESSAdapter({
                    ...defaultEssAdapterConfig,
                    features: 'ta_hierarchy|postal_code|contextual_ta|google|consistent_display'
                  })
                }
              />
            </AbacusVariant>
          </AbacusExperiment>
        </div>
      </ErrorBoundary>
    );
  }

  private formValidator() {
    this.pristine = false;

    return this.validate();
  }

  private validate() {
    const { l10n, model } = this.props;
    const { formatText } = l10n;

    if (this.pristine) {
      return;
    }

    const destination = model && model.destination;
    const focus =
      this.fieldRef &&
      this.fieldRef.current &&
      typeof this.fieldRef.current.focusTypeaheadInput === 'function' &&
      this.fieldRef.current.focusTypeaheadInput;

    if (!destination) {
      return {
        text: formatText('hotels.search.validation.invalidDestination.text'),
        focus
      };
    }
  }
}
